import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Alert from '../components/common/Alert';
import './OrderTimePreference.css';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const hourLabel = (hour) => {
  const part = (value) => {
    const period = value < 12 ? 'AM' : 'PM';
    const n = value % 12 === 0 ? 12 : value % 12;
    return { n, period };
  };
  const start = part(hour);
  const end = part((hour + 1) % 24);
  if (start.period === end.period) return `${start.n}–${end.n} ${start.period}`;
  return `${start.n} ${start.period}–${end.n} ${end.period}`;
};

const HOURS = Array.from({ length: 24 }, (_, hour) => hourLabel(hour));

const formatCount = (value) => new Intl.NumberFormat('en-IN').format(Number(value) || 0);

const formatShare = (count, total) => {
  if (!total) return '0%';
  return `${((Number(count) || 0) / total * 100).toFixed(1)}%`;
};

const cellTone = (value, max) => {
  if (!value || !max) return undefined;
  const strength = value / max;
  const alpha = 0.14 + strength * 0.82;
  return {
    backgroundColor: `rgba(4, 120, 87, ${alpha})`,
    color: strength > 0.62 ? '#ffffff' : '#064e3b'
  };
};

const peakOf = (grid) => {
  let max = 0;
  let hour = 0;
  let day = 0;
  grid.forEach((row, hourIndex) => {
    row.forEach((count, dayIndex) => {
      if (count > max) {
        max = count;
        hour = hourIndex;
        day = dayIndex;
      }
    });
  });
  if (!max) return null;
  return { hour, day, count: max };
};

const PreferenceGrid = ({ window, view }) => {
  const grid = window.grid || [];
  const total = Number(window.totalOrders) || 0;
  const showShare = view === 'percent';
  const show = (count) => (showShare ? formatShare(count, total) : formatCount(count));
  const max = grid.reduce(
    (highest, row) => Math.max(highest, ...row),
    0
  );
  const columnTotals = DAYS.map((_, day) => grid.reduce((sum, row) => sum + (row[day] || 0), 0));
  const peak = peakOf(grid);

  return (
    <section className="timepref-card">
      <div className="timepref-card-head">
        <div>
          <h2>{window.label}</h2>
          <p>{window.rangeLabel}</p>
        </div>
        <div className="timepref-meta">
          <span className={window.status === 'ongoing' ? 'is-ongoing' : 'is-done'}>
            {window.status === 'ongoing' ? 'Current period' : 'Completed'}
          </span>
          <strong>{formatCount(window.totalOrders)} orders</strong>
        </div>
      </div>
      {peak && (
        <p className="timepref-peak">
          Peak: {DAYS[peak.day]}, {HOURS[peak.hour]} · {show(peak.count)}{showShare ? '' : ' orders'}
        </p>
      )}
      <div className="timepref-scroll">
        <table className="timepref-table">
          <thead>
            <tr>
              <th>Time (IST)</th>
              {DAYS.map((day) => <th key={day}>{day}</th>)}
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {HOURS.map((label, hour) => {
              const row = grid[hour] || [];
              const rowTotal = row.reduce((sum, count) => sum + count, 0);
              return (
                <tr key={label}>
                  <th>{label}</th>
                  {DAYS.map((day, dayIndex) => {
                    const count = row[dayIndex] || 0;
                    return (
                      <td key={day} style={cellTone(count, max)}>
                        {show(count)}
                      </td>
                    );
                  })}
                  <td className="is-total">{show(rowTotal)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <th>Total</th>
              {columnTotals.map((total, index) => (
                <td key={DAYS[index]} className="is-total">{show(total)}</td>
              ))}
              <td className="is-total">{show(window.totalOrders)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
};

const OrderTimePreference = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [view, setView] = useState('number');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/order-time-preference', { timeout: 180000 });
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load Time Matrix');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const periods = data?.periods || [];

  return (
    <div className="timepref-page">
      <Header
        title="Time Matrix"
        subtitle="Orders by hour and weekday for P1, P2, and the current period, in IST."
        onRefresh={load}
        loading={loading}
        lastUpdated={data?.generatedAt ? new Date(data.generatedAt) : new Date()}
      />
      <div className="timepref-content">
        {error && <Alert severity="error" message={error} onClose={() => setError(null)} />}
        <div className="timepref-toolbar">
          <label htmlFor="timepref-view">View</label>
          <select
            id="timepref-view"
            value={view}
            onChange={(event) => setView(event.target.value)}
          >
            <option value="number">Number</option>
            <option value="percent">%</option>
          </select>
        </div>
        {periods.map((period) => (
          <PreferenceGrid key={period.id} window={period} view={view} />
        ))}
      </div>
    </div>
  );
};

export default OrderTimePreference;
